import type { HomePageConfig } from '@/types/aggregation/home';
import { DEVICE } from '@/utils/constants';
import { getConfig } from '@/utils/config/config';
import { buildApiUrl, buildUrl } from '@/utils/url/build-url';
import { getCookieValue, COOKIES } from '@/utils/cookies';
import { getBrazeDeviceId } from '@/utils/cookies/braze';
import type { StaticPageConfig } from '@/types/aggregation/static';
import type {
    CategoryV2ApiResponseOf,
    CategoryV2Variant,
    NativeCategoryApiResponseOf
} from '@/types/aggregation/category-v2';
import { checkAndThrowIfNotFound } from '@/utils/not-found/not-found';
import { normalizeRouteForPageConfig } from '@/logic/page-config/normalize-route-for-page-config';
import { cache } from 'react';
import { fetchApi } from './fetcher';

const UNAUTHORIZED_CODE = 'UAG-005';

export const getParams = async (url: string) => {
    const config = getConfig();

    const region = await getCookieValue(COOKIES.REGION);
    const platform = (await getCookieValue(COOKIES.PLATFORM)) || DEVICE.DESKTOP;
    const showAllRegionsToken = await getCookieValue(COOKIES.SHOW_ALL_REGIONS_TOKEN);
    const amplitudeDeviceId = await getCookieValue(COOKIES.AMP_DEVICE_ID);

    const host = buildApiUrl(config.apis.AggregationDataClient);
    const headers: Record<string, string> = {
        'x-region-code': region,
        'x-brand': process.env.BRAND as string,
        accept: 'application/json',
        /**
         * This can be hardcoded since there is also a feature flag behind this in prismic
         */
        'x-api-version': 'v2'
    };

    const cookiesToForward: string[] = [];
    if (showAllRegionsToken) {
        cookiesToForward.push(`${COOKIES.SHOW_ALL_REGIONS_TOKEN}=${showAllRegionsToken}`);
    }
    if (amplitudeDeviceId) {
        cookiesToForward.push(`amplitude-id=${amplitudeDeviceId}`);
    }

    const params: Record<string, string> = {
        product: process.env.PRODUCT as string,
        platform,
        data: 'pageConfig',
        url
    };

    if (cookiesToForward.length > 0) {
        headers['cookie'] = cookiesToForward.join('; ');
    }

    return { host, headers, params, networkConfig: config.network.InternalAggregationClient };
};

export const fetchAllGames = async () => {
    const { host, headers, params, networkConfig } = await getParams('/search-games');

    try {
        const res = await fetchApi(
            buildUrl(host, params),
            {
                method: 'GET',
                headers
            },
            {
                networkConfig
            }
        );

        return await res.json();
    } catch (error) {
        console.error(`(fetchAllGames) Error fetching all games: ${(error as Error).message}`);
        return null;
    }
};

export const fetchRecommendedGames = async () => {
    const { host, headers, params, networkConfig } = await getParams('/recommended-games');

    try {
        const res = await fetchApi(
            buildUrl(host, params),
            {
                method: 'GET',
                headers
            },
            {
                networkConfig
            }
        );

        return await res.json();
    } catch (error) {
        console.error(`(fetchRecommendedGames) Error fetching recommended games: ${(error as Error).message}`);
        return null;
    }
};

export const getPageConfig = cache(async (currentRoute: string = '/'): Promise<HomePageConfig | undefined> => {
    // The page-config endpoint always prepends `<product>-` to the category uid during
    // CMS lookup, so we have to strip the prefix any time the URL already carries one
    // (Epic Hub, Influencer Hub, Gameplay Session) to avoid a doubled `<product>-<product>-<uid>`.
    const normalizedRoute = normalizeRouteForPageConfig(currentRoute);

    const tryFetch = async (excludeCredentials: boolean) => {
        const { host, headers, params, networkConfig } = await getParams(normalizedRoute);

        const brazeDeviceId = await getBrazeDeviceId();
        if (brazeDeviceId) {
            params.deviceId = brazeDeviceId;
        }

        const res = await fetchApi(
            buildUrl(host, params),
            {
                method: 'GET',
                headers,
                signal: AbortSignal.timeout(7000)
            },
            {
                networkConfig,
                excludeCredentials
            }
        );

        return res.json();
    };

    try {
        let json = await tryFetch(false);

        if (json?.code === UNAUTHORIZED_CODE) {
            json = await tryFetch(true);
        }

        return json as HomePageConfig;
    } catch (error) {
        if (error instanceof DOMException && error.name === 'TimeoutError') {
            throw error;
        }

        console.error(
            `(getPageConfig) Error fetching page config for route ${normalizedRoute}: ${(error as Error).message}`
        );
        return undefined;
    }
});

export const fetchRecentlyPlayedZone = async () => {
    const { host, headers, params, networkConfig } = await getParams('/recently-played-games-zone');

    try {
        const res = await fetchApi(
            buildUrl(host, params),
            {
                method: 'GET',
                headers
            },
            {
                networkConfig
            }
        );

        return await res.json();
    } catch (error) {
        console.error(`(fetchRecentlyPlayedZone) Error fetching recently played: ${(error as Error).message}`);
        return null;
    }
};

export const getGameData = async (game: string) => {
    const { host, params, headers, networkConfig } = await getParams(`/game/${game}`);

    try {
        const res = await fetchApi(
            buildUrl(host, params),
            {
                method: 'GET',
                headers
            },
            {
                networkConfig
            }
        );
        return res.json();
    } catch (error) {
        console.error(`(getGameData) Error fetching game data: ${(error as Error).message}`);
        return null;
    }
};

export const getStaticPageData = async (slug: string): Promise<StaticPageConfig | null> => {
    const { host, headers, params, networkConfig } = await getParams(`/${slug}`);
    let json;

    try {
        const res = await fetchApi(
            buildUrl(host, params),
            {
                method: 'GET',
                headers
            },
            {
                networkConfig
            }
        );

        json = await res.json();
    } catch (err) {
        console.error(
            `(getStaticPageData) Error fetching static page data for slug: ${slug} with error: ${(err as Error).message}`
        );
        return null;
    }

    checkAndThrowIfNotFound(json);
    return json as StaticPageConfig;
};

export const getCategoryPageConfig = async (category: string) => {
    const { host, headers, params, networkConfig } = await getParams(`/c/${category}`);
    let json;

    try {
        const res = await fetchApi(
            buildUrl(host, params),
            {
                method: 'GET',
                headers
            },
            {
                networkConfig
            }
        );
        json = await res.json();
    } catch (error) {
        console.error(`(getStaticPageData) Error fetching category page config: ${(error as Error).message}`);
        return null;
    }

    checkAndThrowIfNotFound(json);
    return json;
};

interface GetCategoryV2Data {
    (
        categoryUid: string,
        variant?: 'full',
        includeCategoryGames?: boolean
    ): Promise<CategoryV2ApiResponseOf<'full'> | null>;
    (
        categoryUid: string,
        variant: 'slim',
        includeCategoryGames?: boolean
    ): Promise<CategoryV2ApiResponseOf<'slim'> | null>;
}

export const getCategoryV2Data: GetCategoryV2Data = async (
    categoryUid: string,
    variant: CategoryV2Variant = 'full',
    includeCategoryGames = false
): Promise<CategoryV2ApiResponseOf<CategoryV2Variant> | null> => {
    const { host, headers, params, networkConfig } = await getParams(`/c/${categoryUid}`);
    const { url: _, ...baseParams } = params;
    let json;

    try {
        const res = await fetchApi(
            buildUrl(host, {
                ...baseParams,
                data: 'category-v2',
                uid: categoryUid,
                variant,
                includeCategoryGames: String(includeCategoryGames)
            }),
            {
                method: 'GET',
                headers
            },
            {
                networkConfig
            }
        );
        json = await res.json();
    } catch (error) {
        console.error('(getCategoryV2Data) Error fetching category v2 data:', error);
        return null;
    }

    checkAndThrowIfNotFound(json);
    return json as CategoryV2ApiResponseOf<CategoryV2Variant>;
};

const fetchNativeCategoryData = async (categoryUid: string): Promise<NativeCategoryApiResponseOf<'slim'> | null> => {
    const { host, headers, params, networkConfig } = await getParams(`/c/${categoryUid}`);
    const { url: _, ...baseParams } = params;

    try {
        const res = await fetchApi(
            buildUrl(host, {
                ...baseParams,
                data: 'native-api',
                urn: `ppb:gaming:${baseParams.product}:category`,
                uid: categoryUid,
                variant: 'slim',
                extended: 'true'
            }),
            {
                method: 'GET',
                headers
            },
            {
                networkConfig
            }
        );

        return (await res.json()) as NativeCategoryApiResponseOf<'slim'>;
    } catch (error) {
        console.error('(getNativeCategoryData) Error fetching native category data:', error);
        return null;
    }
};

export const getNativeCategoryData = cache(fetchNativeCategoryData);

export const getSitemap = async () => {
    const { host, headers, params, networkConfig } = await getParams('/sitemap.xml');

    try {
        const res = await fetchApi(
            buildUrl(host, params),
            {
                method: 'GET',
                headers
            },
            {
                networkConfig
            }
        );

        if (!res.headers.get('Content-Type')?.includes('application/json')) {
            return null;
        }

        return res.json();
    } catch (error) {
        console.error(`(getSitemap) Error fetching sitemap: ${(error as Error).message}`);

        return null;
    }
};

export const getServerTime = async (timezone?: string): Promise<string> => {
    const defaultDate = new Date(Date.now()).toString();
    const { host, headers, networkConfig } = await getParams('');
    const serverHost = `${host}/current-time`;

    try {
        const res = await fetchApi(
            buildUrl(serverHost, timezone ? { timezone } : {}),
            {
                method: 'GET',
                headers
            },
            {
                networkConfig
            }
        );

        if (!res.ok) {
            console.warn('Server time request failed', res.status);
            return defaultDate;
        }

        if (!res.headers.get('Content-Type')?.includes('application/json')) {
            console.warn('No JSON content returned by the server');
            return defaultDate;
        }

        const json = await res.json();
        const serverDateTime = json?.datetime ?? json?.data?.datetime;

        if (!serverDateTime) {
            console.warn('Server time response missing datetime field');
            return defaultDate;
        }

        return serverDateTime;
    } catch (error) {
        console.error(`(getServerTime) Error while fetching server time: ${(error as Error).message}`);
        return defaultDate;
    }
};
